import { useCallback, useEffect, useMemo, useState } from 'react';

const INCIDENTS_UI_MODE_STORAGE_KEY_PREFIX = 'warehouse.incidents.presentationMode';

export type IncidentsUiMode = 'operator' | 'advanced';

function getIncidentsUiModeStorageKey(userId: string | null | undefined): string | null {
  const id = String(userId || '').trim();
  if (!id) return null;
  return `${INCIDENTS_UI_MODE_STORAGE_KEY_PREFIX}:${id}`;
}

function readStoredIncidentsUiMode(storageKey: string | null): IncidentsUiMode {
  if (!storageKey) return 'operator';
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === 'advanced' || raw === 'operator') return raw;
  } catch {
    // ignore
  }
  return 'operator';
}

export function useIncidentsUiMode(
  userId: string | null | undefined,
  incidentsAdvancedEligible: boolean,
) {
  const incidentsUiModeStorageKey = useMemo(() => getIncidentsUiModeStorageKey(userId), [userId]);
  const [incidentsUiMode, setIncidentsUiMode] = useState<IncidentsUiMode>('operator');

  useEffect(() => {
    setIncidentsUiMode(readStoredIncidentsUiMode(incidentsUiModeStorageKey));
  }, [incidentsUiModeStorageKey]);

  const setIncidentsUiModePersist = useCallback((mode: IncidentsUiMode) => {
    setIncidentsUiMode(mode);
    if (!incidentsUiModeStorageKey) return;
    try {
      localStorage.setItem(incidentsUiModeStorageKey, mode);
    } catch {
      // ignore
    }
  }, [incidentsUiModeStorageKey]);

  useEffect(() => {
    if (!incidentsAdvancedEligible && incidentsUiMode === 'advanced') {
      setIncidentsUiMode('operator');
      if (!incidentsUiModeStorageKey) return;
      try {
        localStorage.setItem(incidentsUiModeStorageKey, 'operator');
      } catch {
        // ignore
      }
    }
  }, [incidentsAdvancedEligible, incidentsUiMode, incidentsUiModeStorageKey]);

  return {
    incidentsUiMode,
    setIncidentsUiModePersist,
  };
}
